import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Search,
  ShieldCheck,
  AlertCircle,
  Copy,
  Download,
  Plus,
  Trash2,
  Edit3,
  ExternalLink,
  Save,
  X,
  FileText,
  Users,
  CreditCard,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import Logo from '../components/site/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_STORAGE_KEY = 'yb_admin_token';

const slugify = (t) =>
  (t || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// ---------------- Waitlist tab ----------------
const WaitlistTab = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/waitlist`, { headers: { 'X-Admin-Token': token } });
      setEntries(res.data?.entries || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load waitlist.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter((e) => e.email.toLowerCase().includes(query.toLowerCase()));

  const copyAll = () => navigator.clipboard?.writeText(filtered.map((e) => e.email).join('\n'));

  const downloadCsv = () => {
    const rows = [['email', 'source', 'created_at']].concat(
      filtered.map((e) => [e.email, e.source, e.created_at])
    );
    const csv = rows.map((r) => r.map((c) => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `youbuild-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
            Waitlist <span className="italic" style={{ color: 'var(--yb-indigo)' }}>signups</span>
          </h1>
          <p className="mt-1 text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
            {loading ? 'Loading…' : `${filtered.length} of ${entries.length} entries`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyAll} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[13px]" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
            <Copy size={14} /> Copy emails
          </button>
          <button onClick={downloadCsv} className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="mb-4 relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--yb-muted)' }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search email…"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border bg-white text-[14px] outline-none"
          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
        />
      </div>

      {error && (
        <p className="mb-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}>
          <AlertCircle size={14} /> {error}
        </p>
      )}

      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--yb-border)' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: '#FAF9F5' }}>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Email</th>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Source</th>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={3} className="px-5 py-12 text-center text-[14px]" style={{ color: 'var(--yb-muted)' }}>No signups yet.</td></tr>
            )}
            {filtered.map((e, i) => (
              <tr key={e.id || i} className="border-t" style={{ borderColor: 'var(--yb-border)' }}>
                <td className="px-5 py-3 text-[14px] font-medium" style={{ color: 'var(--yb-ink)' }}>{e.email}</td>
                <td className="px-5 py-3 text-[13px]">
                  <span className="px-2 py-0.5 rounded-full text-[12px]" style={{ backgroundColor: '#EEF2FF', color: 'var(--yb-indigo)' }}>{e.source}</span>
                </td>
                <td className="px-5 py-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>{new Date(e.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Blog tab ----------------
const emptyPost = { title: '', slug: '', excerpt: '', body: '', cover_image_url: '', status: 'draft' };

const BlogTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const authHeader = { headers: { 'X-Admin-Token': token } };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/blog/admin/posts`, authHeader);
      setPosts(res.data?.posts || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load posts.');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing('new'); setForm({ ...emptyPost }); setError(''); };
  const startEdit = (p) => { setEditing(p); setForm({ ...emptyPost, ...p, cover_image_url: p.cover_image_url || '' }); setError(''); };
  const cancel = () => { setEditing(null); setForm(emptyPost); };

  const onTitleChange = (v) => {
    setForm((f) => ({
      ...f,
      title: v,
      slug: (editing === 'new' && (!f.slug || f.slug === slugify(f.title))) ? slugify(v) : f.slug,
    }));
  };

  const save = async () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.body.trim()) {
      setError('Title, excerpt and body are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body: form.body,
        cover_image_url: form.cover_image_url || null,
        status: form.status,
      };
      if (editing === 'new') {
        await axios.post(`${API}/blog/admin/posts`, payload, authHeader);
      } else {
        await axios.put(`${API}/blog/admin/posts/${editing.id}`, payload, authHeader);
      }
      await load();
      cancel();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    try {
      const newStatus = p.status === 'published' ? 'draft' : 'published';
      await axios.put(`${API}/blog/admin/posts/${p.id}`, { status: newStatus }, authHeader);
      await load();
    } catch (err) { setError(err?.response?.data?.detail || 'Could not update status.'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/admin/posts/${p.id}`, authHeader);
      await load();
    } catch (err) { setError(err?.response?.data?.detail || 'Could not delete.'); }
  };

  if (editing !== null) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-3xl md:text-4xl" style={{ color: 'var(--yb-ink)' }}>
            {editing === 'new' ? 'New post' : <>Edit <span className="italic" style={{ color: 'var(--yb-indigo)' }}>post</span></>}
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={cancel} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[13px]" style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}>
              <X size={14} /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold disabled:opacity-70">
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {error && (
          <p className="mb-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}><AlertCircle size={14} /> {error}</p>
        )}

        <div className="bg-white rounded-2xl border p-6 space-y-5" style={{ borderColor: 'var(--yb-border)' }}>
          <div>
            <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Title</label>
            <input value={form.title} onChange={(e) => onTitleChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-[15px] outline-none"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>
              Slug <span className="lowercase" style={{ color: 'var(--yb-muted)' }}>(url path)</span>
            </label>
            <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="auto-suggested from title"
              className="w-full px-3 py-2 rounded-lg border text-[14px] font-mono outline-none"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Excerpt <span className="lowercase" style={{ color: 'var(--yb-muted)' }}>(used for SEO meta description)</span></label>
            <textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} rows={2}
              className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none resize-y"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Cover image URL <span className="lowercase" style={{ color: 'var(--yb-muted)' }}>(optional)</span></label>
            <input value={form.cover_image_url} onChange={(e) => setForm((f) => ({ ...f, cover_image_url: e.target.value }))}
              placeholder="https://…"
              className="w-full px-3 py-2 rounded-lg border text-[14px] outline-none"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider mb-1.5" style={{ color: 'var(--yb-muted)' }}>Body <span className="lowercase" style={{ color: 'var(--yb-muted)' }}>(Markdown)</span></label>
            <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={16}
              className="w-full px-3 py-2 rounded-lg border text-[14px] font-mono outline-none resize-y"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Status</label>
            <div className="inline-flex items-center rounded-full border p-1" style={{ borderColor: 'var(--yb-border)' }}>
              {['draft', 'published'].map((s) => (
                <button key={s} onClick={() => setForm((f) => ({ ...f, status: s }))}
                  className="px-3 py-1 text-[12.5px] rounded-full transition-colors"
                  style={{
                    backgroundColor: form.status === s ? 'var(--yb-indigo)' : 'transparent',
                    color: form.status === s ? '#fff' : 'var(--yb-text)',
                  }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
            Blog <span className="italic" style={{ color: 'var(--yb-indigo)' }}>posts</span>
          </h1>
          <p className="mt-1 text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
            {loading ? 'Loading…' : `${posts.length} total`}
          </p>
        </div>
        <button onClick={startNew} className="yb-btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold">
          <Plus size={14} /> New post
        </button>
      </div>

      {error && (
        <p className="mb-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}><AlertCircle size={14} /> {error}</p>
      )}

      <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--yb-border)' }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: '#FAF9F5' }}>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Title</th>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Status</th>
              <th className="text-left px-5 py-3 text-[12px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Published</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 && !loading && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-[14px]" style={{ color: 'var(--yb-muted)' }}>No posts yet. Click “New post”.</td></tr>
            )}
            {posts.map((p) => (
              <tr key={p.id} className="border-t" style={{ borderColor: 'var(--yb-border)' }}>
                <td className="px-5 py-3">
                  <div className="font-medium text-[14px]" style={{ color: 'var(--yb-ink)' }}>{p.title}</div>
                  <div className="text-[12px] font-mono" style={{ color: 'var(--yb-muted)' }}>/{p.slug}</div>
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => toggleStatus(p)} className="px-2 py-0.5 rounded-full text-[12px] cursor-pointer"
                    style={{
                      backgroundColor: p.status === 'published' ? 'rgba(79,70,229,0.12)' : '#F1EFE9',
                      color: p.status === 'published' ? 'var(--yb-indigo)' : 'var(--yb-muted)',
                    }}>
                    {p.status}
                  </button>
                </td>
                <td className="px-5 py-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
                  {p.published_at ? new Date(p.published_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {p.status === 'published' && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"
                        className="p-1.5 rounded-md hover:bg-neutral-100" title="View" style={{ color: 'var(--yb-muted)' }}>
                        <ExternalLink size={14} />
                      </a>
                    )}
                    <button onClick={() => startEdit(p)} className="p-1.5 rounded-md hover:bg-neutral-100" title="Edit" style={{ color: 'var(--yb-indigo)' }}>
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded-md hover:bg-neutral-100" title="Delete" style={{ color: '#B4530A' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Billing Health tab ----------------
const KeyRow = ({ label, meta }) => {
  const present = meta?.present;
  return (
    <div
      className="flex items-center justify-between px-4 py-3 rounded-lg border"
      style={{ borderColor: 'var(--yb-border)', backgroundColor: '#FBFAF7' }}
      data-testid={`billing-key-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>{label}</span>
        <span className="text-[13.5px] font-mono" style={{ color: 'var(--yb-ink)' }}>
          {present ? `${meta.prefix}…${meta.suffix} (${meta.length} chars)` : 'not set'}
        </span>
      </div>
      {present ? (
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: '#065F46' }}>
          <CheckCircle2 size={14} /> Present
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: '#B4530A' }}>
          <XCircle size={14} /> Missing
        </span>
      )}
    </div>
  );
};

const BillingTab = ({ token }) => {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/stripe/diag`, {
        headers: { 'X-Admin-Token': token },
      });
      setDiag(res.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not load billing health.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const secret = diag?.runtime_env?.STRIPE_SECRET_KEY;
  const wh = diag?.runtime_env?.STRIPE_WEBHOOK_SECRET;
  const ping = diag?.stripe_ping;
  const health = diag?.billing_health || {};
  const subs = health.subs_by_plan || {};
  const totalByPlan = Object.entries(subs).map(([plan, statuses]) => ({
    plan,
    total: Object.values(statuses).reduce((a, b) => a + (b || 0), 0),
  }));
  const lastEvent = health.last_webhook_event;

  const keyModeBadge = (() => {
    const km = diag?.key_mode;
    if (km === 'live')    return { label: 'LIVE mode', bg: '#ECFDF5', fg: '#065F46' };
    if (km === 'test')    return { label: 'TEST mode', bg: '#FEF3C7', fg: '#92400E' };
    if (km === 'missing') return { label: 'No key',    bg: '#F3F4F6', fg: '#4B5563' };
    return { label: 'Unknown', bg: '#FEE2E2', fg: '#991B1B' };
  })();

  return (
    <div data-testid="billing-tab">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight" style={{ color: 'var(--yb-ink)' }}>
            Billing <span className="italic" style={{ color: 'var(--yb-indigo)' }}>health</span>
          </h1>
          <p className="mt-1 text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
            Runtime Stripe config, webhook delivery, and subscription mix.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="billing-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg border bg-white"
          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg text-[13px] flex items-center gap-2"
             style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {diag && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Stripe config card */}
          <div className="rounded-xl border p-5 bg-white" style={{ borderColor: 'var(--yb-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[15px]" style={{ color: 'var(--yb-ink)' }}>Stripe config</h2>
              <span
                data-testid="billing-key-mode-badge"
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider"
                style={{ backgroundColor: keyModeBadge.bg, color: keyModeBadge.fg }}
              >
                {keyModeBadge.label}
              </span>
            </div>
            <div className="space-y-2.5">
              <KeyRow label="STRIPE_SECRET_KEY" meta={secret} />
              <KeyRow label="STRIPE_WEBHOOK_SECRET" meta={wh} />
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between text-[12.5px]"
                 style={{ borderColor: 'var(--yb-border)' }}>
              <span style={{ color: 'var(--yb-muted)' }}>Stripe API ping</span>
              {ping?.ok ? (
                <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#065F46' }}>
                  <CheckCircle2 size={13} /> {ping.account_id || 'ok'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 font-semibold" style={{ color: '#991B1B' }}>
                  <XCircle size={13} /> {ping?.error || 'no key'}
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-[12.5px]">
              <span style={{ color: 'var(--yb-muted)' }}>Webhook handler enabled</span>
              <span className="font-semibold" data-testid="billing-webhook-enabled"
                    style={{ color: diag.webhook_enabled ? '#065F46' : '#991B1B' }}>
                {diag.webhook_enabled ? 'YES' : 'NO'}
              </span>
            </div>
          </div>

          {/* Webhook activity card */}
          <div className="rounded-xl border p-5 bg-white" style={{ borderColor: 'var(--yb-border)' }}>
            <h2 className="font-semibold text-[15px] mb-4" style={{ color: 'var(--yb-ink)' }}>Webhook activity</h2>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>Events received (24h)</div>
                <div className="text-3xl font-semibold" data-testid="billing-events-24h" style={{ color: 'var(--yb-ink)' }}>
                  {health.events_received_24h ?? 0}
                </div>
              </div>
              <div className="pt-3 border-t" style={{ borderColor: 'var(--yb-border)' }}>
                <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: 'var(--yb-muted)' }}>Last event</div>
                {lastEvent ? (
                  <div className="text-[13px]" data-testid="billing-last-event">
                    <div className="font-mono" style={{ color: 'var(--yb-ink)' }}>{lastEvent.event_type}</div>
                    <div className="text-[11.5px]" style={{ color: 'var(--yb-muted)' }}>
                      {new Date(lastEvent.received_at).toLocaleString()} · {lastEvent.event_id}
                    </div>
                  </div>
                ) : (
                  <div className="text-[13px]" style={{ color: 'var(--yb-muted)' }} data-testid="billing-last-event-none">
                    No webhook deliveries recorded yet.
                    {diag.webhook_enabled ? ' Send a test event from Stripe Dashboard to confirm.'
                                          : ' Configure STRIPE_WEBHOOK_SECRET first.'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Subscription mix card */}
          <div className="rounded-xl border p-5 bg-white lg:col-span-2" style={{ borderColor: 'var(--yb-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[15px]" style={{ color: 'var(--yb-ink)' }}>Subscription mix</h2>
              {health.manual_override_count > 0 && (
                <span
                  data-testid="billing-manual-override-count"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold"
                  style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                >
                  <AlertCircle size={12} /> {health.manual_override_count} manual override{health.manual_override_count === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['free', 'pro', 'enterprise', 'owner'].map((plan) => {
                const total = totalByPlan.find((r) => r.plan === plan)?.total || 0;
                const statuses = subs[plan] || {};
                return (
                  <div key={plan} className="rounded-lg border p-3" style={{ borderColor: 'var(--yb-border)' }}
                       data-testid={`billing-plan-${plan}`}>
                    <div className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>{plan}</div>
                    <div className="text-2xl font-semibold" style={{ color: 'var(--yb-ink)' }}>{total}</div>
                    <div className="text-[11px] mt-1" style={{ color: 'var(--yb-muted)' }}>
                      {Object.entries(statuses).map(([s, c]) => `${s}: ${c}`).join(' · ') || '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Main Admin ----------------
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [inputToken, setInputToken] = useState('');
  const [tab, setTab] = useState('waitlist');
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    // Validate by making an authenticated request
    try {
      await axios.get(`${API}/waitlist`, { headers: { 'X-Admin-Token': inputToken.trim() } });
      localStorage.setItem(TOKEN_STORAGE_KEY, inputToken.trim());
      setToken(inputToken.trim());
      setError('');
    } catch (err) {
      setError('Invalid admin token.');
    }
  };

  const logout = () => { localStorage.removeItem(TOKEN_STORAGE_KEY); setToken(''); };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: 'var(--yb-bg)' }}>
        <div className="w-full max-w-md bg-white rounded-2xl border p-8" style={{ borderColor: 'var(--yb-border)' }}>
          <div style={{ color: 'var(--yb-ink)' }}><Logo /></div>
          <h1 className="font-serif text-3xl mt-6" style={{ color: 'var(--yb-ink)' }}>Admin sign in</h1>
          <p className="mt-2 text-[14px]" style={{ color: 'var(--yb-muted)' }}>Enter your admin token to manage the waitlist and blog.</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-3">
            <input type="password" value={inputToken} onChange={(e) => setInputToken(e.target.value)}
              placeholder="Admin token"
              className="w-full px-4 py-2.5 rounded-lg border text-[14px] outline-none"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }} />
            <button type="submit" className="yb-btn-primary w-full py-2.5 rounded-lg font-semibold text-[14px]">Sign in</button>
          </form>
          {error && (
            <p className="mt-4 text-[13px] flex items-center gap-2" style={{ color: '#B4530A' }}>
              <AlertCircle size={14} /> {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--yb-bg)' }}>
      <div className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" style={{ color: 'var(--yb-ink)' }}><Logo /></a>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border p-1" style={{ borderColor: 'var(--yb-border)', backgroundColor: '#fff' }}>
              <button onClick={() => setTab('waitlist')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] transition-colors"
                style={{
                  backgroundColor: tab === 'waitlist' ? 'var(--yb-indigo)' : 'transparent',
                  color: tab === 'waitlist' ? '#fff' : 'var(--yb-text)',
                }}>
                <Users size={13} /> Waitlist
              </button>
              <button onClick={() => setTab('blog')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] transition-colors"
                style={{
                  backgroundColor: tab === 'blog' ? 'var(--yb-indigo)' : 'transparent',
                  color: tab === 'blog' ? '#fff' : 'var(--yb-text)',
                }}>
                <FileText size={13} /> Blog
              </button>
              <button onClick={() => setTab('billing')}
                data-testid="admin-tab-billing"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] transition-colors"
                style={{
                  backgroundColor: tab === 'billing' ? 'var(--yb-indigo)' : 'transparent',
                  color: tab === 'billing' ? '#fff' : 'var(--yb-text)',
                }}>
                <CreditCard size={13} /> Billing
              </button>
            </div>
            <button onClick={logout} className="text-[13px]" style={{ color: 'var(--yb-muted)' }}>Sign out</button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck size={16} style={{ color: 'var(--yb-indigo)' }} />
          <span className="text-[12px] uppercase tracking-widest" style={{ color: 'var(--yb-muted)' }}>Admin</span>
        </div>

        {tab === 'waitlist' && <WaitlistTab token={token} />}
        {tab === 'blog' && <BlogTab token={token} />}
        {tab === 'billing' && <BillingTab token={token} />}
      </div>
    </div>
  );
};

export default Admin;
